import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { useState, useEffect } from "react";
import { useI18n } from "../../i18n/i18n";
import CurrencySelector from "../CurrencySelector";
import { ChevronDown, Download, Calculator, TrendingUp, ArrowRight, CheckCircle2 } from "lucide-react";
import { useAuthCheck } from "../../hooks/useAuthCheck";
import { useAuth } from "../AuthProvider";
import { AuthModal } from "../AuthModal";
import { generateMonthlySavingsPDF } from "../../lib/monthlySavingsPdfExport";
import { LanguageLink } from "../LanguageLink";

interface SavingsBreakdown {
  needs: number;
  wants: number;
  savings: number;
}

const MonthlySavingsCalculator = () => {
  const { t } = useI18n();
  const { user } = useAuth();
  const {
    checkAuthAndExecute,
    isCheckingAuth,
    showAuthModal,
    setShowAuthModal,
    pendingCallback,
  } = useAuthCheck();
  const [salary, setSalary] = useState<string>("1500");
  const [otherIncome, setOtherIncome] = useState<string>("0");
  const [salaryFrequency, setSalaryFrequency] = useState<string>("monthly");
  const [currency, setCurrency] = useState<string>("EUR");
  const [breakdown, setBreakdown] = useState<SavingsBreakdown | null>(null);
  // Store original breakdown in the base currency (EUR)
  const [originalBreakdown, setOriginalBreakdown] =
    useState<SavingsBreakdown | null>(null);
  // Track original currency
  const [originalCurrency, setOriginalCurrency] = useState<string>("EUR");
  const [authMode, setAuthMode] = useState<"login" | "signup">("login");

  const eurToUsdRate = 1.08; // 1 EUR = 1.08 USD
  const usdToEurRate = 0.93; // 1 USD = 0.93 EUR

  const calculateBreakdown = () => {
    // If annual is selected, we're interpreting the input as a monthly amount
    // that we need to annualize before calculating the breakdown
    let calculationBase;
    // Parse string inputs to numbers, defaulting to 0 if empty or NaN
    const salaryNum = salary === "" ? 0 : parseFloat(salary) || 0;
    const otherIncomeNum =
      otherIncome === "" ? 0 : parseFloat(otherIncome) || 0;
    let totalIncome = salaryNum + otherIncomeNum;

    if (salaryFrequency === "annual") {
      // For annual frequency, the input is monthly but we show annual results
      calculationBase = totalIncome * 12;
    } else {
      // For monthly frequency, the input is monthly and we show monthly results
      calculationBase = totalIncome;
    }

    const needs = calculationBase * 0.5;
    const wants = calculationBase * 0.2;
    const savings = calculationBase * 0.3;

    const newBreakdown = { needs, wants, savings };
    setBreakdown(newBreakdown);
    // Store the original breakdown and currency
    setOriginalBreakdown(newBreakdown);
    setOriginalCurrency(currency);
  };

  const handleCurrencyChange = (newCurrency: string) => {
    // Allow currency change regardless of breakdown existence
    if (originalBreakdown && breakdown) {
      // Always convert from the original values to the new currency
      if (newCurrency !== originalCurrency) {
        setBreakdown({
          needs: convertAmount(
            originalBreakdown.needs,
            originalCurrency,
            newCurrency,
          ),
          wants: convertAmount(
            originalBreakdown.wants,
            originalCurrency,
            newCurrency,
          ),
          savings: convertAmount(
            originalBreakdown.savings,
            originalCurrency,
            newCurrency,
          ),
        });
      } else {
        // If switching back to original currency, use original values
        setBreakdown(originalBreakdown);
      }
    }
    setCurrency(newCurrency);
  };

  const formatCurrency = (amount: number) => {
    if (amount === undefined || amount === null) return "—";

    return new Intl.NumberFormat(
      currency === "USD" ? "en-US" : currency === "GBP" ? "en-GB" : "de-DE",
      {
        style: "currency",
        currency: currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      },
    ).format(amount);
  };

  const convertAmount = (
    amount: number,
    fromCurrency: string,
    toCurrency: string,
  ): number => {
    if (fromCurrency === toCurrency) {
      return amount;
    }

    if (fromCurrency === "EUR" && toCurrency === "USD") {
      return amount * eurToUsdRate;
    }

    if (fromCurrency === "USD" && toCurrency === "EUR") {
      return amount * usdToEurRate;
    }

    // Add more conversion rates as needed (e.g., EUR to GBP, USD to GBP, etc.)
    // Handle cases where a direct conversion rate isn't available
    // For simplicity, you might want to convert to a common currency (like USD) first.

    return amount; // If no conversion is defined, return original amount
  };

  const handleExportResults = () => {
    const executeExport = () => {
      if (!breakdown) return;

      const salaryNum = salary === "" ? 0 : parseFloat(salary) || 0;
      const otherIncomeNum =
        otherIncome === "" ? 0 : parseFloat(otherIncome) || 0;

      const exportData = {
        salary: salaryNum,
        otherIncome: otherIncomeNum,
        salaryFrequency,
        needs: breakdown.needs,
        wants: breakdown.wants,
        savings: breakdown.savings,
        currency,
      };

      const doc = generateMonthlySavingsPDF(exportData, currency, t);
      doc.save(
        `monthly-savings-breakdown-${new Date().toISOString().split("T")[0]}.pdf`,
      );
    };

    checkAuthAndExecute(executeExport);
  };

  // Effect to execute pending callback after successful authentication
  useEffect(() => {
    if (user && pendingCallback && !showAuthModal) {
      pendingCallback();
    }
  }, [user, pendingCallback, showAuthModal]);

  return (
    <div className="container mx-auto px-4 md:px-6">
      <div className="max-w-5xl mx-auto">
        <h2 className="text-2xl md:text-3xl font-bold text-neutral-800 text-center mb-6">
          {t("tools.savings.title")}
        </h2>
        <p className="text-neutral-600 text-center mb-8 px-2">
          {t("tools.savings.description")}
        </p>

        <div className="flex flex-col gap-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Input section */}
            <Card className="bg-neutral-100 rounded-xl h-fit">
              <CardContent className="p-4 sm:p-8 space-y-6 flex flex-col items-center">
                <h3 className="font-semibold text-lg">
                  {t("tools.savings.netIncome")}
                </h3>

                <form className="w-full space-y-5">
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <Label htmlFor="salary">
                        {t("tools.savings.familyIncome")} (
                        {currency === "USD" ? "$" : "€"})
                      </Label>
                      <CurrencySelector
                        value={currency}
                        onChange={handleCurrencyChange}
                      />
                    </div>
                    <Input
                      id="salary"
                      type="text"
                      inputMode="decimal"
                      value={salary}
                      onChange={(e) => setSalary(e.target.value)}
                      className="calculator-input w-full border border-neutral-300 focus:outline-none focus:ring-1 focus:ring-[#EA580C]"
                    />
                  </div>

                  <div>
                    <Label htmlFor="otherIncome" className="mb-2 block">
                      {t("tools.savings.otherIncome")} (
                      {currency === "USD" ? "$" : "€"})
                    </Label>
                    <Input
                      id="otherIncome"
                      type="text"
                      inputMode="decimal"
                      value={otherIncome}
                      onChange={(e) => setOtherIncome(e.target.value)}
                      className="calculator-input w-full border border-neutral-300 focus:outline-none focus:ring-1 focus:ring-[#EA580C]"
                    />
                  </div>

                  <div className="flex flex-col min-h-[48px]">
                    <Label htmlFor="salaryFrequency" className="mb-2 block">
                      {t("tools.savings.salaryFrequency")}
                    </Label>
                    <Select
                      value={salaryFrequency}
                      onValueChange={(value) => {
                        setSalaryFrequency(value);
                        // Clear breakdown when frequency changes to avoid showing incorrect results
                        setBreakdown(null);
                      }}
                    >
                      <SelectTrigger className="calculator-input w-full h-10 border border-neutral-300 focus:outline-none focus:ring-1 focus:ring-[#EA580C]">
                        <SelectValue>
                          {salaryFrequency === "monthly"
                            ? t("tools.savings.monthly")
                            : t("tools.savings.annual")}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="monthly">
                          {t("tools.savings.monthly")}
                        </SelectItem>
                        <SelectItem value="annual">
                          {t("tools.savings.annual")}
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <Button
                    onClick={calculateBreakdown}
                    type="button"
                    className="w-full bg-[#EA580C] hover:bg-[#C2410C] text-white font-medium"
                  >
                    {t("tools.savings.calculate")}
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Results section */}
            <Card className="bg-white rounded-xl border border-neutral-200 shadow-sm overflow-auto h-fit">
              <CardContent className="pt-6 px-4 sm:px-6">
                <h3 className="font-semibold text-lg mb-4 pb-2 border-b border-neutral-200">
                  {t("tools.savings.breakdown")}
                </h3>

                {!breakdown ? (
                  <div className="text-center py-6 text-neutral-500 opacity-60 px-2">
                    {t("tools.savings.calculateToSeeResults")}
                  </div>
                ) : (
                  <div className="space-y-4">
                    <Accordion type="single" collapsible className="w-full">
                      <AccordionItem value="needs" className="border-0">
                        <AccordionTrigger className="p-0 hover:no-underline w-full">
                          <div className="p-4 bg-green-50 rounded-lg w-full flex justify-between items-center transition-all duration-200 hover:bg-green-100 hover:shadow-md">
                            <div className="flex-1">
                              <div className="text-sm text-green-600 mb-1">
                                {t("tools.savings.needs")}
                              </div>
                              <div className="text-xl font-bold text-green-700">
                                {formatCurrency(breakdown.needs)}
                              </div>
                              <div className="text-xs text-green-600 mt-1">
                                50% - {t("tools.savings.needsDesc")}
                              </div>
                            </div>
                            <ChevronDown className="h-4 w-4 shrink-0 transition-transform duration-200 text-green-600" />
                          </div>
                        </AccordionTrigger>
                        <AccordionContent className="pt-3 pb-0 mt-2 border-t border-green-200">
                          <div className="text-sm text-green-700">
                            <h4 className="font-medium mb-2">
                              {t("tools.savings.needsExamplesTitle")}
                            </h4>
                            <div className="mb-3 overflow-x-auto">
                              <table className="w-full text-sm">
                                <thead>
                                  <tr>
                                    <th className="text-left pb-2 w-[42%] pr-4">
                                      {t("tools.savings.concept")}
                                    </th>
                                    <th className="text-center pb-2 w-[16%] px-4">
                                      {t("tools.savings.percentage")}
                                    </th>
                                    <th className="text-center pb-2 w-[42%]">
                                      {t("tools.savings.amount")}
                                    </th>
                                  </tr>
                                </thead>
                                <tbody className="text-green-700">
                                  <tr>
                                    <td className="pr-2">
                                      {t("tools.savings.needsEx1")}
                                    </td>
                                    <td className="px-2 text-center whitespace-nowrap">
                                      30-35%
                                    </td>
                                    <td className="text-center pl-2 whitespace-nowrap">
                                      {formatCurrency(
                                        (breakdown?.needs / 0.5) * 0.3,
                                      )}{" "}
                                      -{" "}
                                      {formatCurrency(
                                        (breakdown?.needs / 0.5) * 0.35,
                                      )}
                                    </td>
                                  </tr>
                                  <tr>
                                    <td className="pr-2">
                                      {t("tools.savings.needsEx2")}
                                    </td>
                                    <td className="px-2 text-center whitespace-nowrap">
                                      7-12%
                                    </td>
                                    <td className="text-center pl-2 whitespace-nowrap">
                                      {formatCurrency(
                                        (breakdown?.needs / 0.5) * 0.07,
                                      )}{" "}
                                      -{" "}
                                      {formatCurrency(
                                        (breakdown?.needs / 0.5) * 0.12,
                                      )}
                                    </td>
                                  </tr>
                                  <tr>
                                    <td className="pr-2">
                                      {t("tools.savings.needsEx3")}
                                    </td>
                                    <td className="px-2 text-center whitespace-nowrap">
                                      4-8%
                                    </td>
                                    <td className="text-center pl-2 whitespace-nowrap">
                                      {formatCurrency(
                                        (breakdown?.needs / 0.5) * 0.04,
                                      )}{" "}
                                      -{" "}
                                      {formatCurrency(
                                        (breakdown?.needs / 0.5) * 0.08,
                                      )}
                                    </td>
                                  </tr>
                                  <tr>
                                    <td className="pr-2">
                                      {t("tools.savings.needsEx4")}
                                    </td>
                                    <td className="px-2 text-center whitespace-nowrap">
                                      3-10%
                                    </td>
                                    <td className="text-center pl-2 whitespace-nowrap">
                                      {formatCurrency(
                                        (breakdown?.needs / 0.5) * 0.03,
                                      )}{" "}
                                      -{" "}
                                      {formatCurrency(
                                        (breakdown?.needs / 0.5) * 0.1,
                                      )}
                                    </td>
                                  </tr>
                                  <tr>
                                    <td className="pr-2">
                                      {t("tools.savings.needsEx5")}
                                    </td>
                                    <td className="px-2 text-center whitespace-nowrap">
                                      2-6%
                                    </td>
                                    <td className="text-center pl-2 whitespace-nowrap">
                                      {formatCurrency(
                                        (breakdown?.needs / 0.5) * 0.02,
                                      )}{" "}
                                      -{" "}
                                      {formatCurrency(
                                        (breakdown?.needs / 0.5) * 0.06,
                                      )}
                                    </td>
                                  </tr>
                                  <tr>
                                    <td className="pr-2">
                                      {t("tools.savings.otherEssentials")}
                                    </td>
                                    <td className="px-2 text-center whitespace-nowrap">
                                      1-3%
                                    </td>
                                    <td className="text-center pl-2 whitespace-nowrap">
                                      {formatCurrency(
                                        (breakdown?.needs / 0.5) * 0.01,
                                      )}{" "}
                                      -{" "}
                                      {formatCurrency(
                                        (breakdown?.needs / 0.5) * 0.03,
                                      )}
                                    </td>
                                  </tr>
                                </tbody>
                              </table>
                            </div>
                            <p className="mt-2 text-xs italic">
                              {t("tools.savings.needsTip")}
                            </p>
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    </Accordion>

                    <Accordion type="single" collapsible>
                      <AccordionItem value="savings" className="border-0">
                        <AccordionTrigger className="p-0 hover:no-underline w-full">
                          <div className="p-4 bg-purple-50 rounded-lg w-full flex justify-between items-center transition-all duration-200 hover:bg-purple-100 hover:shadow-md">
                            <div className="flex-1">
                              <div className="text-sm text-purple-600 mb-1">
                                {t("tools.savings.savings")}
                              </div>
                              <div className="text-xl font-bold text-purple-700">
                                {formatCurrency(breakdown.savings)}
                              </div>
                              <div className="text-xs text-purple-600 mt-1">
                                30% - {t("tools.savings.savingsDesc")}
                              </div>
                            </div>
                            <ChevronDown className="h-4 w-4 shrink-0 transition-transform duration-200 text-purple-600" />
                          </div>
                        </AccordionTrigger>
                        <AccordionContent className="pt-3 pb-0 mt-2 border-t border-purple-200">
                          <div className="text-sm text-purple-700">
                            <h4 className="font-medium mb-2">
                              {t("tools.savings.savingsExamplesTitle")}
                            </h4>
                            <div className="overflow-x-auto">
                              <ul className="list-disc pl-5 space-y-1">
                                <li>{t("tools.savings.savingsEx1")}</li>
                                <li>{t("tools.savings.savingsEx2")}</li>
                                <li>{t("tools.savings.savingsEx3")}</li>
                                <li>{t("tools.savings.savingsEx4")}</li>
                                <li>{t("tools.savings.savingsEx5")}</li>
                              </ul>
                            </div>
                            <p className="mt-2 text-xs italic">
                              {t("tools.savings.savingsTip")}
                            </p>
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    </Accordion>

                    <Accordion type="single" collapsible>
                      <AccordionItem value="wants" className="border-0">
                        <AccordionTrigger className="p-0 hover:no-underline w-full">
                          <div className="p-4 bg-blue-50 rounded-lg w-full flex justify-between items-center transition-all duration-200 hover:bg-blue-100 hover:shadow-md">
                            <div className="flex-1">
                              <div className="text-sm text-blue-600 mb-1">
                                {t("tools.savings.wants")}
                              </div>
                              <div className="text-xl font-bold text-blue-700">
                                {formatCurrency(breakdown.wants)}
                              </div>
                              <div className="text-xs text-blue-600 mt-1">
                                20% - {t("tools.savings.wantsDesc")}
                              </div>
                            </div>
                            <ChevronDown className="h-4 w-4 shrink-0 transition-transform duration-200 text-blue-600" />
                          </div>
                        </AccordionTrigger>
                        <AccordionContent className="pt-3 pb-0 mt-2 border-t border-blue-200">
                          <div className="text-sm text-blue-700">
                            <h4 className="font-medium mb-2">
                              {t("tools.savings.wantsExamplesTitle")}
                            </h4>
                            <div className="overflow-x-auto">
                              <ul className="list-disc pl-5 space-y-1">
                                <li>{t("tools.savings.wantsEx1")}</li>
                                <li>{t("tools.savings.wantsEx2")}</li>
                                <li>{t("tools.savings.wantsEx3")}</li>
                                <li>{t("tools.savings.wantsEx4")}</li>
                                <li>{t("tools.savings.wantsEx5")}</li>
                              </ul>
                            </div>
                            <p className="mt-2 text-xs italic">
                              {t("tools.savings.wantsTip")}
                            </p>
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    </Accordion>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>

        <div className="text-center mb-10 mt-12">
          <Button
            variant="ghost"
            className={`inline-flex items-center font-medium ${
              !breakdown || isCheckingAuth
                ? "text-neutral-400 cursor-not-allowed"
                : "text-neutral-700 hover:text-[#EA580C] cursor-pointer"
            }`}
            onClick={handleExportResults}
            disabled={!breakdown || isCheckingAuth}
          >
            <Download className="mr-1 h-4 w-4" />
            {isCheckingAuth ? t("common.loading") : t("common.exportResults")}
            {!user && (
              <span className="ml-1 text-xs bg-blue-500 text-white px-2 py-0.5 rounded-full">
                {t("common.loginRequired")}
              </span>
            )}
          </Button>
        </div>

        {/* Educational Content Section */}
        <div className="mt-16 space-y-12">
          {/* What is 50/30/20 Rule */}
          <div className="prose max-w-none">
            <h2 className="text-3xl font-bold text-neutral-900 mb-6">
              {t("tools.savings.whatIs502020Title")}
            </h2>
            <div className="bg-white rounded-lg p-8 border border-neutral-200 shadow-sm">
              <p className="text-lg text-neutral-700 leading-relaxed mb-6">
                {t("tools.savings.whatIs502020Desc1")}
              </p>
              <p className="text-lg text-neutral-700 leading-relaxed">
                {t("tools.savings.whatIs502020Desc2")}
              </p>
            </div>
          </div>

          {/* How it works */}
          <div className="prose max-w-none">
            <h2 className="text-3xl font-bold text-neutral-900 mb-6">
              {t("tools.savings.howItWorksTitle")}
            </h2>
            <div className="bg-gradient-to-br from-slate-50 via-blue-50/30 to-slate-50 rounded-xl p-8 border border-slate-200">
              <p className="text-lg text-neutral-700 leading-relaxed mb-6">
                {t("tools.savings.howItWorksDesc")}
              </p>

              <div className="space-y-4">
                <div className="bg-white rounded-lg p-5 border border-slate-200">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl font-bold text-[#FF6600]">1.</span>
                    <div>
                      <p className="text-neutral-700 leading-relaxed">
                        {t("tools.savings.howItWorksStep1")}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-lg p-5 border border-slate-200">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl font-bold text-[#FF6600]">2.</span>
                    <div>
                      <p className="text-neutral-700 leading-relaxed">
                        {t("tools.savings.howItWorksStep2")}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-lg p-5 border border-slate-200">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl font-bold text-[#FF6600]">3.</span>
                    <div>
                      <p className="text-neutral-700 leading-relaxed">
                        {t("tools.savings.howItWorksStep3")}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-lg p-5 border border-slate-200">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl font-bold text-[#FF6600]">4.</span>
                    <div>
                      <p className="text-neutral-700 leading-relaxed">
                        {t("tools.savings.howItWorksStep4")}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Practical Tips */}
          <div className="prose max-w-none">
            <h2 className="text-3xl font-bold text-neutral-900 mb-6">
              {t("tools.savings.practicalTipsTitle")}
            </h2>
            <div className="bg-white rounded-lg p-8 border border-neutral-200 shadow-sm">
              <p className="text-lg text-neutral-700 leading-relaxed mb-8">
                {t("tools.savings.practicalTipsIntro")}
              </p>

              <div className="space-y-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-2xl">💰</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-neutral-900 mb-2">
                      {t("tools.savings.tip1Title")}
                    </h3>
                    <p className="text-neutral-700 leading-relaxed">
                      {t("tools.savings.tip1Desc")}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-2xl">📈</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-neutral-900 mb-2">
                      {t("tools.savings.tip2Title")}
                    </h3>
                    <p className="text-neutral-700 leading-relaxed">
                      {t("tools.savings.tip2Desc")}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-2xl">📚</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-neutral-900 mb-2">
                      {t("tools.savings.tip3Title")}
                    </h3>
                    <p className="text-neutral-700 leading-relaxed">
                      {t("tools.savings.tip3Desc")}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Common Mistakes */}
          <div className="prose max-w-none">
            <h2 className="text-3xl font-bold text-neutral-900 mb-6">
              {t("tools.savings.commonMistakesTitle")}
            </h2>
            <div className="space-y-5">
              <div className="flex items-center gap-4">
                <div className="w-7 h-7 bg-red-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold text-sm">×</span>
                </div>
                <div>
                  <h4 className="text-lg font-bold text-neutral-900 mb-2">
                    {t("tools.savings.mistake1Title")}
                  </h4>
                  <p className="text-neutral-700 leading-relaxed">
                    {t("tools.savings.mistake1Desc")}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="w-7 h-7 bg-red-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold text-sm">×</span>
                </div>
                <div>
                  <h4 className="text-lg font-bold text-neutral-900 mb-2">
                    {t("tools.savings.mistake2Title")}
                  </h4>
                  <p className="text-neutral-700 leading-relaxed">
                    {t("tools.savings.mistake2Desc")}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="w-7 h-7 bg-red-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold text-sm">×</span>
                </div>
                <div>
                  <h4 className="text-lg font-bold text-neutral-900 mb-2">
                    {t("tools.savings.mistake3Title")}
                  </h4>
                  <p className="text-neutral-700 leading-relaxed">
                    {t("tools.savings.mistake3Desc")}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Why Control Income and Expenses */}
          <div className="prose max-w-none">
            <h2 className="text-3xl font-bold text-neutral-900 mb-6">
              {t("tools.savings.whyControlTitle")}
            </h2>
            <div className="bg-white rounded-lg p-8 border border-neutral-200 shadow-sm">
              <p className="text-lg text-neutral-700 leading-relaxed mb-6">
                {t("tools.savings.whyControlDesc")}
              </p>
              <div className="space-y-4 mb-6">
                <div className="flex items-center gap-3 p-4 bg-blue-50 rounded-lg">
                  <CheckCircle2 className="h-6 w-6 text-blue-600 flex-shrink-0" />
                  <p className="text-neutral-800 leading-relaxed">
                    {t("tools.savings.whyControlBenefit1")}
                  </p>
                </div>
                <div className="flex items-center gap-3 p-4 bg-green-50 rounded-lg">
                  <CheckCircle2 className="h-6 w-6 text-green-600 flex-shrink-0" />
                  <p className="text-neutral-800 leading-relaxed">
                    {t("tools.savings.whyControlBenefit2")}
                  </p>
                </div>
                <div className="flex items-center gap-3 p-4 bg-purple-50 rounded-lg">
                  <CheckCircle2 className="h-6 w-6 text-purple-600 flex-shrink-0" />
                  <p className="text-neutral-800 leading-relaxed">
                    {t("tools.savings.whyControlBenefit3")}
                  </p>
                </div>
              </div>
              <p className="text-lg text-neutral-700 leading-relaxed">
                {t("tools.savings.whyControlConclusion")}
              </p>
            </div>
          </div>

          {/* How to Reduce Expenses */}
          <div className="prose max-w-none">
            <h2 className="text-3xl font-bold text-neutral-900 mb-6">
              {t("tools.savings.reduceExpensesTitle")}
            </h2>
            <div className="bg-white rounded-lg p-8 border border-neutral-200 shadow-sm">
              <p className="text-lg text-neutral-700 leading-relaxed mb-8">
                {t("tools.savings.reduceExpensesIntro")}
              </p>

              {/* Conscious Spending */}
              <div className="mb-8">
                <h3 className="text-2xl font-bold text-neutral-900 mb-4 flex items-center gap-2">
                  <span className="text-2xl">💡</span>
                  {t("tools.savings.consciousSpendingTitle")}
                </h3>
                <p className="text-neutral-700 leading-relaxed mb-4">
                  {t("tools.savings.consciousSpendingDesc")}
                </p>
                <div className="bg-gradient-to-r from-orange-50 to-orange-100/50 rounded-lg p-5 border-l-4 border-[#FF6600]">
                  <p className="text-neutral-800 leading-relaxed">
                    <span className="font-semibold">{t("tools.compound.practicalExample")}:</span> {t("tools.savings.consciousSpendingExample")}
                  </p>
                </div>
              </div>

              {/* Where to Start */}
              <div className="mb-8">
                <h3 className="text-2xl font-bold text-neutral-900 mb-4 flex items-center gap-2">
                  <span className="text-2xl">🎯</span>
                  {t("tools.savings.whereToStartTitle")}
                </h3>
                <p className="text-neutral-700 leading-relaxed mb-4">
                  {t("tools.savings.whereToStartDesc")}
                </p>
                <p className="text-neutral-700 leading-relaxed mb-4">
                  {t("tools.savings.fixedExpensesExamples")}
                </p>
                <div className="bg-amber-50 border-l-4 border-amber-500 p-5 rounded-r-lg">
                  <p className="text-neutral-800 leading-relaxed">
                    <span className="font-semibold">⚠️ {t("tools.salary.step2Note")}:</span> {t("tools.savings.avoidMistake")}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* How to Increase Income */}
          <div className="prose max-w-none">
            <h2 className="text-3xl font-bold text-neutral-900 mb-6">
              {t("tools.savings.increaseIncomeTitle")}
            </h2>
            <div className="bg-white rounded-lg p-8 border border-neutral-200 shadow-sm">
              <p className="text-lg text-neutral-700 leading-relaxed mb-8">
                {t("tools.savings.increaseIncomeIntro")}
              </p>

              <div className="space-y-6">
                <div className="flex items-center gap-4">
                  <div className="w-8 h-8 bg-[#FF6600] text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">
                    1
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-neutral-900 mb-2">
                      {t("tools.savings.improveSalaryTitle")}
                    </h3>
                    <p className="text-neutral-700 leading-relaxed">
                      {t("tools.savings.improveSalaryDesc")}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <div className="w-8 h-8 bg-[#FF6600] text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">
                    2
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-neutral-900 mb-2">
                      {t("tools.savings.careerPlanningTitle")}
                    </h3>
                    <p className="text-neutral-700 leading-relaxed">
                      {t("tools.savings.careerPlanningDesc")}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <div className="w-8 h-8 bg-[#FF6600] text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">
                    3
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-neutral-900 mb-2">
                      {t("tools.savings.skillsTitle")}
                    </h3>
                    <p className="text-neutral-700 leading-relaxed">
                      {t("tools.savings.skillsDesc")}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <div className="w-8 h-8 bg-[#FF6600] text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">
                    4
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-neutral-900 mb-2">
                      {t("tools.savings.alternativeIncomeTitle")}
                    </h3>
                    <p className="text-neutral-700 leading-relaxed">
                      {t("tools.savings.alternativeIncomeDesc")}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 5 Key Savings Tips */}
          <div className="prose max-w-none">
            <h2 className="text-3xl font-bold text-neutral-900 mb-6">
              {t("tools.savings.savingsTipsTitle")}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-gradient-to-br from-white to-green-50/30 rounded-xl p-6 border border-green-200 hover:border-green-400 transition-all hover:shadow-lg">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-green-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-white text-xl font-bold">1</span>
                  </div>
                  <h3 className="text-lg font-bold text-neutral-900">
                    {t("tools.savings.savingsTip1Title")}
                  </h3>
                </div>
                <p className="text-neutral-700 leading-relaxed text-sm">
                  {t("tools.savings.savingsTip1Desc")}
                </p>
              </div>

              <div className="bg-gradient-to-br from-white to-blue-50/30 rounded-xl p-6 border border-blue-200 hover:border-blue-400 transition-all hover:shadow-lg">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-white text-xl font-bold">2</span>
                  </div>
                  <h3 className="text-lg font-bold text-neutral-900">
                    {t("tools.savings.savingsTip2Title")}
                  </h3>
                </div>
                <p className="text-neutral-700 leading-relaxed text-sm">
                  {t("tools.savings.savingsTip2Desc")}
                </p>
              </div>

              <div className="bg-gradient-to-br from-white to-purple-50/30 rounded-xl p-6 border border-purple-200 hover:border-purple-400 transition-all hover:shadow-lg">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-white text-xl font-bold">3</span>
                  </div>
                  <h3 className="text-lg font-bold text-neutral-900">
                    {t("tools.savings.savingsTip3Title")}
                  </h3>
                </div>
                <p className="text-neutral-700 leading-relaxed text-sm">
                  {t("tools.savings.savingsTip3Desc")}
                </p>
              </div>

              <div className="bg-gradient-to-br from-white to-orange-50/30 rounded-xl p-6 border border-orange-200 hover:border-[#FF6600] transition-all hover:shadow-lg">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-gradient-to-br from-[#FF6600] to-[#FF7700] rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-white text-xl font-bold">4</span>
                  </div>
                  <h3 className="text-lg font-bold text-neutral-900">
                    {t("tools.savings.savingsTip4Title")}
                  </h3>
                </div>
                <p className="text-neutral-700 leading-relaxed text-sm">
                  {t("tools.savings.savingsTip4Desc")}
                </p>
              </div>

              <div className="md:col-span-2 bg-gradient-to-br from-white to-slate-50/50 rounded-xl p-6 border border-slate-200 hover:border-slate-400 transition-all hover:shadow-lg">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-gradient-to-br from-slate-500 to-slate-600 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-white text-xl font-bold">5</span>
                  </div>
                  <h3 className="text-lg font-bold text-neutral-900">
                    {t("tools.savings.savingsTip5Title")}
                  </h3>
                </div>
                <p className="text-neutral-700 leading-relaxed text-sm">
                  {t("tools.savings.savingsTip5Desc")}
                </p>
              </div>
            </div>
          </div>

          {/* What to Do with Saved Money */}
          <div className="prose max-w-none">
            <h2 className="text-3xl font-bold text-neutral-900 mb-6">
              {t("tools.savings.savedMoneyTitle")}
            </h2>
            <div className="bg-gradient-to-br from-slate-50 via-blue-50/30 to-slate-50 rounded-xl p-8 border border-slate-200">
              <p className="text-lg text-neutral-700 leading-relaxed mb-6">
                {t("tools.savings.savedMoneyIntro")}
              </p>
              <div className="bg-white rounded-lg p-6 mb-6 border border-slate-200">
                <p className="text-neutral-800 leading-relaxed mb-4">
                  {t("tools.savings.savedMoneyImportance")}
                </p>
                <p className="text-neutral-800 leading-relaxed">
                  {t("tools.savings.savedMoneyPurpose")}
                </p>
              </div>
              <div className="bg-gradient-to-r from-[#FF6600]/10 via-orange-50/50 to-[#FF6600]/10 rounded-lg p-6 border-l-4 border-[#FF6600]">
                <p className="text-neutral-800 font-medium leading-relaxed">
                  💡 {t("tools.savings.savedMoneyConclusion")}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Related Content Section */}
        <div className="mt-16 space-y-8">
          {/* Other Calculators Section */}
          <div className="bg-gradient-to-br from-slate-50 to-white rounded-xl p-8 border border-slate-200">
            <div className="flex items-center gap-2 mb-6">
              <Calculator className="h-6 w-6 text-[#FF6600]" />
              <h2 className="text-2xl font-bold text-neutral-900">
                {t("tools.savings.relatedCalculators")}
              </h2>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <LanguageLink
                to="/tools/net-salary"
                className="group p-4 bg-white rounded-lg border border-neutral-200 hover:border-[#FF6600] hover:shadow-md transition-all duration-200"
              >
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-semibold text-neutral-900 group-hover:text-[#FF6600] transition-colors">
                      {t("tools.savings.netSalaryCalculator")}
                    </h3>
                    <p className="text-sm text-neutral-600 mt-1">
                      {t("tools.savings.netSalaryDesc")}
                    </p>
                  </div>
                  <ArrowRight className="h-5 w-5 text-neutral-400 group-hover:text-[#FF6600] group-hover:translate-x-1 transition-all" />
                </div>
              </LanguageLink>

              <LanguageLink
                to="/tools/compound-interest"
                className="group p-4 bg-white rounded-lg border border-neutral-200 hover:border-[#FF6600] hover:shadow-md transition-all duration-200"
              >
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-semibold text-neutral-900 group-hover:text-[#FF6600] transition-colors">
                      {t("tools.savings.compoundInterestCalculator")}
                    </h3>
                    <p className="text-sm text-neutral-600 mt-1">
                      {t("tools.savings.compoundInterestDesc")}
                    </p>
                  </div>
                  <ArrowRight className="h-5 w-5 text-neutral-400 group-hover:text-[#FF6600] group-hover:translate-x-1 transition-all" />
                </div>
              </LanguageLink>

              <LanguageLink
                to="/tools/mortgage-calculator"
                className="group p-4 bg-white rounded-lg border border-neutral-200 hover:border-[#FF6600] hover:shadow-md transition-all duration-200"
              >
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-semibold text-neutral-900 group-hover:text-[#FF6600] transition-colors">
                      {t("tools.savings.mortgageCalculator")}
                    </h3>
                    <p className="text-sm text-neutral-600 mt-1">
                      {t("tools.savings.mortgageDesc")}
                    </p>
                  </div>
                  <ArrowRight className="h-5 w-5 text-neutral-400 group-hover:text-[#FF6600] group-hover:translate-x-1 transition-all" />
                </div>
              </LanguageLink>

              <LanguageLink
                to="/tools/salary-comparison"
                className="group p-4 bg-white rounded-lg border border-neutral-200 hover:border-[#FF6600] hover:shadow-md transition-all duration-200"
              >
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-semibold text-neutral-900 group-hover:text-[#FF6600] transition-colors">
                      {t("tools.savings.salaryComparisonCalculator")}
                    </h3>
                    <p className="text-sm text-neutral-600 mt-1">
                      {t("tools.savings.salaryComparisonDesc")}
                    </p>
                  </div>
                  <ArrowRight className="h-5 w-5 text-neutral-400 group-hover:text-[#FF6600] group-hover:translate-x-1 transition-all" />
                </div>
              </LanguageLink>
            </div>
          </div>

          {/* Related Articles Section */}
          <div className="bg-gradient-to-br from-blue-50 to-white rounded-xl p-8 border border-blue-200">
            <div className="flex items-center gap-2 mb-6">
              <TrendingUp className="h-6 w-6 text-[#FF6600]" />
              <h2 className="text-2xl font-bold text-neutral-900">
                {t("tools.savings.relatedArticles")}
              </h2>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* First Article - Takes 2 columns */}
              <LanguageLink
                to="/resources/emergency-fund-article"
                className="md:col-span-2 group relative rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-500 h-[280px]"
              >
                <img 
                  src="https://images.unsplash.com/photo-1633158829585-23ba8f7c8caf?q=80&w=800&auto=format&fit=crop" 
                  alt="Emergency fund concept"
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-black/20 group-hover:from-black/80 group-hover:via-black/40 transition-all duration-500" />
                <div className="absolute inset-0 p-6 flex flex-col justify-end">
                  <div className="transform translate-y-2 group-hover:translate-y-0 transition-transform duration-300">
                    <h3 className="text-xl md:text-2xl font-bold text-white mb-2 leading-tight">
                      {t("tools.savings.article1Title")}
                    </h3>
                    <p className="text-white/90 mb-3 text-sm leading-relaxed line-clamp-2">
                      {t("tools.savings.article1Desc")}
                    </p>
                    <span className="inline-flex items-center text-xs font-semibold text-white bg-[#FF6600] px-3 py-1.5 rounded-lg group-hover:bg-[#DD5500] transition-colors">
                      {t("tools.savings.readMore")}
                      <ArrowRight className="h-3 w-3 ml-2 group-hover:translate-x-1 transition-transform" />
                    </span>
                  </div>
                </div>
              </LanguageLink>

              {/* Second Article */}
              <LanguageLink
                to="/resources/monthly-saving-challenges-article"
                className="group relative block rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-500 h-[280px]"
              >
                <img 
                  src="https://images.unsplash.com/photo-1579621970588-a35d0e7ab9b6?q=80&w=500&auto=format&fit=crop" 
                  alt="Monthly saving challenges with piggy bank and calendar"
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent group-hover:from-black/70 transition-all duration-500" />
                <div className="absolute inset-0 p-5 flex flex-col justify-end">
                  <h3 className="text-lg font-bold text-white mb-2 leading-tight">
                    {t("tools.savings.article2Title")}
                  </h3>
                  <span className="inline-flex items-center self-start text-xs font-semibold text-white bg-[#FF6600] px-3 py-1.5 rounded-lg group-hover:bg-[#DD5500] transition-colors">
                    {t("tools.savings.readMore")}
                    <ArrowRight className="h-3 w-3 ml-2 group-hover:translate-x-1 transition-transform" />
                  </span>
                </div>
              </LanguageLink>

              {/* Third Article */}
              <LanguageLink
                to="/resources/impulse-buying-article"
                className="group relative block rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-500 h-[280px]"
              >
                <img 
                  src="https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?q=80&w=500&auto=format&fit=crop" 
                  alt="Impulse buying and psychology of money"
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent group-hover:from-black/70 transition-all duration-500" />
                <div className="absolute inset-0 p-5 flex flex-col justify-end">
                  <h3 className="text-lg font-bold text-white mb-2 leading-tight">
                    {t("tools.savings.article3Title")}
                  </h3>
                  <span className="inline-flex items-center self-start text-xs font-semibold text-white bg-[#FF6600] px-3 py-1.5 rounded-lg group-hover:bg-[#DD5500] transition-colors">
                    {t("tools.savings.readMore")}
                    <ArrowRight className="h-3 w-3 ml-2 group-hover:translate-x-1 transition-transform" />
                  </span>
                </div>
              </LanguageLink>

              {/* Fourth Article - Spans remaining space */}
              <LanguageLink
                to="/resources/start-investing-article"
                className="md:col-span-2 lg:col-span-4 group relative rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-500 h-[200px]"
              >
                <img 
                  src="https://images.unsplash.com/photo-1579532537598-459ecdaf39cc?q=80&w=800&auto=format&fit=crop" 
                  alt="Young professional starting investment journey with laptop showing financial dashboard and growth charts"
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/60 to-black/30 group-hover:from-black/80 group-hover:via-black/50 transition-all duration-500" />
                <div className="absolute inset-0 p-6 flex flex-col justify-center items-start max-w-2xl">
                  <h3 className="text-xl md:text-2xl font-bold text-white mb-2 leading-tight">
                    {t("tools.savings.article4Title")}
                  </h3>
                  <p className="text-white/90 mb-3 text-sm leading-relaxed line-clamp-2">
                    {t("tools.savings.article4Desc")}
                  </p>
                  <span className="inline-flex items-center text-xs font-semibold text-white bg-[#FF6600] px-3 py-1.5 rounded-lg group-hover:bg-[#DD5500] transition-colors">
                    {t("tools.savings.readMore")}
                    <ArrowRight className="h-3 w-3 ml-2 group-hover:translate-x-1 transition-transform" />
                  </span>
                </div>
              </LanguageLink>
            </div>
          </div>
        </div>

        {/* Authentication Modal */}
        <AuthModal
          isOpen={showAuthModal}
          onClose={() => setShowAuthModal(false)}
          mode={authMode}
          onModeChange={setAuthMode}
        />
      </div>
    </div>
  );
};

export default MonthlySavingsCalculator;
